import { createContext, useState, useContext, useEffect } from "react";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [user, setUser] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Inicialización
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");
      const savedMenus = localStorage.getItem("menus");
      
      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setToken(savedToken);

          if (savedMenus) {
            setMenus(JSON.parse(savedMenus));
          }

          // Verificar token y actualizar
          try {
            const userData = await authService.getUser();
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));

            const userMenusResponse = await authService.getUserMenus();
            const userMenus = userMenusResponse?.menus || [];
            // ✅ Si ya vienen anidados, los usamos tal cual
            if (Array.isArray(userMenus)) {
              setMenus(userMenus);
              localStorage.setItem("menus", JSON.stringify(userMenus));
            }
          } catch (error) {
            console.error("Token inválido, limpiando sesión");
            logout();
          }
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("menus");
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // ✅ Login
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authService.login({ email, password });

      if (response.access_token) {
        setToken(response.access_token);
        setUser(response.user);

        // Cargar menús anidados
        const userMenusResponse = await authService.getUserMenus();
        const userMenus = userMenusResponse?.menus || [];
        setMenus(Array.isArray(userMenus) ? userMenus : []);
        localStorage.setItem("menus", JSON.stringify(userMenus));

        const role = response.user.roles?.[0]?.name || "usuario";
        const messages = {
          admin: "¡Bienvenido Administrador!",
          transportista: "¡Bienvenido Transportista!",
          operador_logistico: "¡Bienvenido Operador Logístico!",
          cliente: "¡Bienvenido Cliente!"
        };

        toast.success(messages[role] || `¡Bienvenido ${response.user.name}!`, {
          duration: 4000,
          position: "top-right",
          style: { background: '#10b981', color: '#fff' },
        });

        return { success: true, user: response.user };
      } else {
        throw new Error("No se recibió token de acceso");
      }
    } catch (error) {
      toast.error(error.message || "Error al iniciar sesión", {
        duration: 4000,
        position: "top-right",
        style: { background: '#ef4444', color: '#fff' },
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Logout
  const logout = async () => {
    const userName = user?.name || "Usuario";
    try {
      await authService.logout();
    } catch (error) {
      console.error("Error en logout:", error);
    }

    setToken(null);
    setUser(null);
    setMenus([]);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("menus");

    toast.success(`Hasta luego ${userName}`, {
      duration: 3000,
      position: "top-right",
      style: { background: '#10b981', color: '#fff' },
    });
  };

  // ✅ Permisos de menú
  const hasMenuPermission = (menuSlug, permission = "can_view") => {
    const findMenu = (items) => {
      for (const m of items) {
        if (m.slug === menuSlug) return m;
        if (m.children?.length) {
          const found = findMenu(m.children);
          if (found) return found;
        }
      }
      return null;
    };

    const menu = findMenu(menus);
    return menu ? menu.permissions?.[permission] || false : false;
  };

  // ✅ Como ya viene anidado, usamos directamente `menus`
  const getMenuStructure = () => menus;

  const hasRole = (roleName) => user?.roles?.some(r => r.name === roleName) || false;
  const isAdmin = () => hasRole("admin");
  const getUserRole = () => user?.roles?.[0]?.name || null;

  const getUserData = () => user ? {
    id: user.id,
    nombre: user.name,
    email: user.email,
    tipo: getUserRole(),
    empresa_id: user.empresa_id,
    empresa: user.empresa,
    permissions: user.permissions || [],
    avatar: user.avatar
  } : null;

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        menus,
        login,
        logout,
        loading,
        isAuthenticated: !!token,
        hasMenuPermission,
        getMenuStructure,
        hasRole,
        isAdmin,
        getUserRole,
        getUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return context;
};
